// NodeBox — renders a single topology node as an SVG <g> element.
// Visual design matches journey-a-timeline.jsx L475-519.

import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectIsNodeVisited,
  selectIsNodeActive,
  selectActualPath,
  selectSimulationStatus,
  selectEffectiveAssertionResults,
  setActiveEventIndex,
} from '../../store/slices/simulationSlice.ts';
import {
  selectNode,
  hoverNode,
  clearHover,
  selectSelectedNode,
  selectHoveredNode,
} from '../../store/slices/uiSlice.ts';
import { selectEvents } from '../../store/slices/eventsSlice.ts';
import { selectNodeTypeStyles } from '../../store/slices/topologySlice.ts';
import {
  resolveNodeStyle,
  resolveNodeRx,
  resolveNodeInk,
  NDD_PALETTE,
} from '../../services/styleResolver.ts';
import { useInformationDensity } from './useInformationDensity.ts';
import { formatStepChip, buildAssertionPills } from './runStory.ts';
import type { NodeDef } from '../../types/index.ts';
import type { NodePosition } from './layoutEngine.ts';
import type { RootState } from '../../store/index.ts';

export interface NodeBoxProps {
  node: NodeDef;
  pos: NodePosition;
}

export default function NodeBox({ node, pos }: NodeBoxProps) {
  const dispatch = useDispatch();
  const isVisited = useSelector((state: RootState) => selectIsNodeVisited(state, node.id));
  const isActive = useSelector((state: RootState) => selectIsNodeActive(state, node.id));
  const selectedNode = useSelector(selectSelectedNode);
  const hoveredNode = useSelector(selectHoveredNode);

  const actualPath = useSelector(selectActualPath);
  const simulationStatus = useSelector(selectSimulationStatus);
  const events = useSelector(selectEvents);

  const assertionResults = useSelector(selectEffectiveAssertionResults);
  const topologyStyles = useSelector(selectNodeTypeStyles);
  const { showNodeLabels } = useInformationDensity();

  // Resolve topology→scenario node ID for cross-namespace comparisons
  const reverseNodeMap = useSelector(
    (state: RootState) => state.topology.topologyToScenarioNodeMap,
  );
  const scenarioNodeId = reverseNodeMap[node.id] ?? null;

  const isSelected =
    selectedNode === node.id || (scenarioNodeId != null && selectedNode === scenarioNodeId);
  const isHovered =
    hoveredNode === node.id || (scenarioNodeId != null && hoveredNode === scenarioNodeId);

  const ts = resolveNodeStyle(node.type, topologyStyles);
  const nodeStepIndices = actualPath
    .map((nid: string, i: number) =>
      nid === node.id || (scenarioNodeId && nid === scenarioNodeId) ? i : -1,
    )
    .filter((i: number) => i >= 0);

  // Count assertions per node. Skip records (feature-detected — the store may
  // mark results as skipped) count as neither passed nor failed.
  let nodeAssertionCount = 0;
  let nodeFailureCount = 0;
  let nodeSkipCount = 0;
  for (const stepIdx of nodeStepIndices) {
    for (const r of assertionResults) {
      const matches =
        r.assertionId === `path-step-${stepIdx}` ||
        r.assertionId.startsWith(`boundary-${stepIdx}-`) ||
        r.assertionId.startsWith(`schema-${stepIdx}-`) ||
        r.assertionId.startsWith(`branch-eval-${stepIdx}-`) ||
        r.assertionId.startsWith(`terminal-${stepIdx}-`);
      if (matches) {
        if ((r as { skipped?: boolean }).skipped === true) {
          nodeSkipCount++;
          continue;
        }
        nodeAssertionCount++;
        if (!r.passed) nodeFailureCount++;
      }
    }
  }
  const hasFailedAssertion = nodeFailureCount > 0;
  // Pill row: ✓n / —n / ✗n — only non-zero pills. A merely visited node with
  // zero assertions gets no pills and keeps neutral visited styling.
  const assertionPills = buildAssertionPills(
    nodeAssertionCount - nodeFailureCount,
    nodeSkipCount,
    nodeFailureCount,
  );

  // "Unreached" state: simulation completed but this node was never visited
  const isSimCompleted = simulationStatus === 'completed' || simulationStatus === 'failed';
  const isUnreached = isSimCompleted && nodeStepIndices.length === 0 && !isActive;

  const handleClick = useCallback(() => {
    if (isSelected) {
      dispatch(selectNode(null));
      // Restore activeEventIndex to the walker's current position
      const walkerIdx = actualPath.length > 0 ? actualPath.length - 1 : null;
      dispatch(setActiveEventIndex(walkerIdx));
    } else {
      dispatch(selectNode(node.id));
      // Try walker path first — resolve topology ID to scenario ID
      const scenarioId = reverseNodeMap[node.id] ?? node.id;
      const idx = Math.max(actualPath.lastIndexOf(node.id), actualPath.lastIndexOf(scenarioId));
      if (idx >= 0) {
        dispatch(setActiveEventIndex(idx));
      } else if (node.eventTypes && node.eventTypes.length > 0) {
        // Fallback: find first event matching this node's eventTypes
        const eventTypeSet = new Set(node.eventTypes);
        const eventIdx = events.findIndex((e) => eventTypeSet.has(e.eventType));
        dispatch(setActiveEventIndex(eventIdx >= 0 ? eventIdx : null));
      } else {
        dispatch(setActiveEventIndex(null));
      }
    }
  }, [dispatch, node.id, node.eventTypes, isSelected, actualPath, reverseNodeMap, events]);

  const handleMouseEnter = useCallback(() => {
    dispatch(hoverNode(node.id));
  }, [dispatch, node.id]);

  const handleMouseLeave = useCallback(() => {
    dispatch(clearHover());
  }, [dispatch]);

  const rx = resolveNodeRx(node.type, topologyStyles);

  // Palette tiles: the fill is always the type's NDD palette colour and the
  // label is always its ink. Run state is conveyed by outline weight, glow and
  // opacity — never by changing the hue.
  const ink = resolveNodeInk(ts);
  const onDarkTile = ink === '#ffffff';
  const baseStroke = onDarkTile ? 'rgba(255, 255, 255, 0.45)' : 'rgba(26, 26, 26, 0.35)';
  const fillColor = ts.fill;
  let fillOpacity = 1;
  let strokeColor = baseStroke;
  let strokeOpacity = 1;
  let strokeWidth = 1;
  let glow: string | undefined;
  const labelColor = ink;

  if (isActive) {
    strokeColor = 'var(--accent)';
    strokeWidth = 3;
    glow = 'drop-shadow(0 0 5px var(--accent))';
  } else if (isSelected) {
    strokeColor = 'var(--indigo)';
    strokeWidth = 2.5;
  } else if (isHovered) {
    strokeWidth = 1.5;
    strokeColor = onDarkTile ? 'rgba(255, 255, 255, 0.8)' : 'rgba(26, 26, 26, 0.7)';
  } else if (isVisited) {
    strokeWidth = 1.5;
    strokeColor = onDarkTile ? 'rgba(255, 255, 255, 0.7)' : 'rgba(26, 26, 26, 0.6)';
  }

  // Unvisited-while-running dimming — once a run is underway, nodes the
  // walker has not touched recede (opacity only) so the visited path reads as
  // the story.
  const wasWalked = isVisited || nodeStepIndices.length > 0;
  if (simulationStatus !== 'idle' && !wasWalked && !isActive && !isSelected && !isHovered) {
    fillOpacity = 0.45;
    strokeOpacity = 0.6;
  }

  // Assertion failure override — red stroke on visited nodes with failures
  if (hasFailedAssertion && isVisited) {
    strokeColor = 'var(--red)';
    strokeOpacity = 1;
    strokeWidth = 2;
  }

  // Unreached override — dimmed, dashed for nodes not exercised
  if (isUnreached && !isSelected && !isHovered) {
    fillOpacity = 0.35;
    strokeOpacity = 0.5;
  }

  const truncatedLabel = node.label.length > 22 ? node.label.slice(0, 21) + '…' : node.label;

  // ── Run-story overlays ──────────────────────────────────────────────────
  // Sequence chip (top-left corner, straddling the top edge): 1-based step
  // order; revisited nodes show joined indices ("4↩12").
  const stepChipText = nodeStepIndices.length > 0 ? formatStepChip(nodeStepIndices) : null;
  const isRevisit = nodeStepIndices.length > 1;
  const chipH = 11;
  const chipY = pos.y - chipH / 2;
  const chipX = pos.x + 10;
  const chipW = stepChipText ? stepChipText.length * 4.6 + 8 : 0;

  // Assertion pills (top-right corner, right-aligned): ✓n / —n / ✗n.
  const pillH = 11;
  const pillY = pos.y - pillH / 2;
  const pillGap = 2;
  const nodeRight = pos.x + 6 + pos.w;
  const pillWidths = assertionPills.map((p) => 13 + String(p.count).length * 4.5);
  const pillXs: number[] = [];
  {
    const totalW =
      pillWidths.reduce((a, b) => a + b, 0) + Math.max(0, assertionPills.length - 1) * pillGap;
    let px = nodeRight - 2 - totalW;
    for (const w of pillWidths) {
      pillXs.push(px);
      px += w + pillGap;
    }
  }
  const causeText = node.cause
    ? `[${node.cause.length > 20 ? node.cause.slice(0, 19) + '…' : node.cause}]`
    : '';
  const causeW = causeText.length * 4.6 + 6;
  const pillTitle = `${nodeAssertionCount - nodeFailureCount} passed · ${nodeSkipCount} skipped · ${nodeFailureCount} failed assertions at this node`;

  return (
    <g
      data-node-id={node.id}
      data-node-type={node.type}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ cursor: 'pointer' }}
      role="button"
      tabIndex={0}
      aria-label={`${ts.label}: ${node.label}`}
      aria-pressed={isSelected}
    >
      <rect
        x={pos.x + 6}
        y={pos.y}
        width={pos.w}
        height={pos.h}
        rx={rx}
        fill={fillColor}
        fillOpacity={fillOpacity}
        stroke={strokeColor}
        strokeOpacity={strokeOpacity}
        strokeWidth={strokeWidth}
        strokeDasharray={node.optional ? '3,2' : isUnreached ? '4,3' : 'none'}
        style={glow ? { filter: glow } : undefined}
      />
      {/* Owner edge (Moment ADR-040 D1): a process event or command is the Process
          swatch down the tile's left edge, in place of an aggregate marker. */}
      {node.process && (
        <rect
          data-testid="process-edge"
          x={pos.x + 6}
          y={pos.y}
          width={5}
          height={pos.h}
          rx={Math.min(rx, 2.5)}
          fill={NDD_PALETTE.process.fill}
          fillOpacity={fillOpacity}
        >
          <title>{`Process: ${node.process}`}</title>
        </rect>
      )}
      {/* Type icon */}
      <text
        x={pos.x + 24}
        y={pos.y + pos.h / 2 + 1}
        dominantBaseline="middle"
        fill={ink}
        fontSize="11"
        fontFamily="'JetBrains Mono', monospace"
      >
        {ts.icon}
      </text>
      {/* Label (hidden at ecosystem zoom) */}
      {showNodeLabels && (
        <text
          x={pos.x + 38}
          y={pos.y + pos.h / 2 + 1}
          dominantBaseline="middle"
          fill={labelColor}
          fontSize="10"
          fontWeight={isSelected || isActive ? 600 : 400}
          fontFamily="'JetBrains Mono', monospace"
        >
          {truncatedLabel}
        </text>
      )}
      {/* Sequence chip — 1-based step order, dual indices on revisits */}
      {showNodeLabels && stepChipText && (
        <g data-testid="step-chip" data-steps={stepChipText}>
          <title>
            {isRevisit
              ? `Visited at steps ${nodeStepIndices.map((i: number) => i + 1).join(' and ')} (revisit)`
              : `Step ${nodeStepIndices[0] + 1} of the run`}
          </title>
          <rect
            x={chipX}
            y={chipY}
            width={chipW}
            height={chipH}
            rx={3}
            fill="var(--bg-pill)"
            stroke={isRevisit ? 'rgba(251, 191, 36, 0.55)' : 'rgba(129, 140, 248, 0.45)'}
            strokeWidth={1}
          />
          <text
            x={chipX + chipW / 2}
            y={pos.y + 1}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={isRevisit ? 'var(--amber)' : 'var(--indigo)'}
            fontSize="7"
            fontWeight={700}
            fontFamily="'JetBrains Mono', monospace"
          >
            {stepChipText}
          </text>
        </g>
      )}
      {/* Cause chip (Moment ADR-040 D2): the cause a command is placed for,
          bottom edge, so the same command reads differently per flow. */}
      {showNodeLabels && node.cause && (
        <g data-testid="cause-chip" data-cause={node.cause}>
          <title>{`Placed for cause ${node.cause}`}</title>
          <rect
            x={nodeRight - causeW - 2}
            y={pos.y + pos.h - 5.5}
            width={causeW}
            height={11}
            rx={3}
            fill="#ffffff"
            stroke="rgba(26, 26, 26, 0.5)"
            strokeWidth={0.75}
          />
          <text
            x={nodeRight - causeW / 2 - 2}
            y={pos.y + pos.h}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#1a1a1a"
            fontSize="7"
            fontWeight={700}
            fontFamily="'JetBrains Mono', monospace"
          >
            {causeText}
          </text>
        </g>
      )}
      {/* Assertion pills — ✓n (green round) / —n (gray dashed) / ✗n (red sharp).
          Shape AND color differ per kind (colorblind-safe); only non-zero pills. */}
      {assertionPills.length > 0 && (
        <g data-testid="assertion-pills">
          <title>{pillTitle}</title>
          {assertionPills.map((pill, i) => {
            const px = pillXs[i];
            const pw = pillWidths[i];
            const isFail = pill.kind === 'fail';
            const isSkip = pill.kind === 'skip';
            return (
              <g key={pill.kind} data-testid={`assertion-pill-${pill.kind}`}>
                <rect
                  x={px}
                  y={pillY}
                  width={pw}
                  height={pillH}
                  rx={isFail ? 2 : 5.5}
                  fill="var(--bg-pill)"
                />
                <rect
                  x={px}
                  y={pillY}
                  width={pw}
                  height={pillH}
                  rx={isFail ? 2 : 5.5}
                  fill={isFail ? 'var(--red)' : isSkip ? 'transparent' : 'rgba(74, 222, 128, 0.16)'}
                  stroke={isFail ? 'none' : isSkip ? 'var(--text-muted)' : 'var(--green)'}
                  strokeWidth={1}
                  strokeOpacity={isSkip ? 0.8 : 0.75}
                  strokeDasharray={isSkip ? '2,1.5' : 'none'}
                />
                <text
                  x={px + pw / 2}
                  y={pillY + pillH / 2 + 1}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={isFail ? '#fff' : isSkip ? 'var(--text-muted)' : 'var(--green)'}
                  fontSize="7"
                  fontWeight={700}
                  fontFamily="'JetBrains Mono', monospace"
                >
                  {pill.glyph}
                  {pill.count}
                </text>
              </g>
            );
          })}
        </g>
      )}
    </g>
  );
}
